export type DetailField = {
  label: string
  value?: string
  /** Renders the value as a person chip with a link affordance. */
  person?: boolean
  /** Renders a colored status dot instead of text. */
  indicator?: 'green' | 'amber' | 'red'
}

export type LoanDetailSections = {
  information: { left: DetailField[]; right: DetailField[] }
  team: { left: DetailField[]; right: DetailField[] }
}

/** Field-level detail behind each loan, mirroring the Salesforce loan record layout. */
export const loanDetails: Record<string, LoanDetailSections> = {
  'ln-1191': {
    information: {
      left: [
        { label: 'Last Contact Attempt', value: 'Aug 27, 2026 · 4:10 PM' },
        { label: 'Loan Purpose', value: 'Purchase' },
        { label: 'Loan Type (1st TD)', value: 'Conventional' },
        { label: 'Loan Term', value: '30' },
        { label: 'Rate (1st TD)', value: '6.375%' },
        { label: 'Monthly Payment (1st TD)', value: '$3,194.00' },
        { label: 'LTV', value: '80.000%' },
        { label: 'CLTV', value: '80.000%' },
        { label: 'MERS MIN Number', value: '100392610009471182' },
        { label: 'Status Indicator', indicator: 'green' },
      ],
      right: [
        { label: 'Lender (1st)', value: 'UWM' },
        { label: 'Lender Loan #', value: '1526391156' },
        { label: 'FHA/VA Case #' },
        { label: 'Property Type', value: 'Single Family Residence' },
        { label: 'Loan Program (1st TD)', value: 'Conventional 30 Year Fixed' },
        { label: 'Purchase Price', value: '$640,000.00' },
        { label: 'Loan Amount (1st TD)', value: '$512,000.00' },
        { label: 'DTI Front End', value: '24.318%' },
        { label: 'DTI Back End', value: '33.107%' },
        { label: 'Indicator Reason', value: '2 Days in App Received' },
      ],
    },
    team: {
      left: [
        { label: 'Loan Officer', value: 'Milan Rakic', person: true },
        { label: 'Loan Officer Assistant', value: 'Handy Nunez', person: true },
        { label: 'Loan Officer Number', value: '(305) 514-5242' },
        { label: 'Loan Processor #1', value: 'Renee Perez', person: true },
        { label: 'Manager', value: 'Calvin Dado', person: true },
        { label: 'Processor Number', value: '(305) 514-5290' },
        { label: '3rd Party Ordering' },
      ],
      right: [
        { label: 'Appraisal Coordinator', value: 'Nate Zook', person: true },
        { label: 'Lock Desk' },
        { label: 'Vendor Coordinator', value: 'Nate Zook', person: true },
        { label: 'Processing Manager' },
        { label: 'Closing Coordinator' },
        { label: 'Funding Coordinator' },
        { label: 'Post-Closer' },
      ],
    },
  },
  'ln-1184': {
    information: {
      left: [
        { label: 'Last Contact Attempt', value: 'Aug 28, 2026 · 8:45 AM' },
        { label: 'Loan Purpose', value: 'Purchase' },
        { label: 'Loan Type (1st TD)', value: 'FHA' },
        { label: 'Loan Term', value: '30' },
        { label: 'Rate (1st TD)', value: '6.125%' },
        { label: 'Monthly Payment (1st TD)', value: '$1,672.00' },
        { label: 'LTV', value: '96.500%' },
        { label: 'CLTV', value: '96.500%' },
        { label: 'MERS MIN Number', value: '100392610009468340' },
        { label: 'Status Indicator', indicator: 'amber' },
      ],
      right: [
        { label: 'Lender (1st)', value: 'UWM' },
        { label: 'Lender Loan #', value: '1526388420' },
        { label: 'FHA/VA Case #', value: '105-8842119-703' },
        { label: 'Property Type', value: 'Town House' },
        { label: 'Loan Program (1st TD)', value: 'FHA 30 Year Fixed' },
        { label: 'Purchase Price', value: '$285,000.00' },
        { label: 'Loan Amount (1st TD)', value: '$275,000.00' },
        { label: 'DTI Front End', value: '28.940%' },
        { label: 'DTI Back End', value: '41.220%' },
        { label: 'Indicator Reason', value: '3 Days in Credit & Pricing' },
      ],
    },
    team: {
      left: [
        { label: 'Loan Officer', value: 'Milan Rakic', person: true },
        { label: 'Loan Officer Assistant', value: 'Handy Nunez', person: true },
        { label: 'Loan Officer Number', value: '(305) 514-5242' },
        { label: 'Loan Processor #1' },
        { label: 'Manager', value: 'Calvin Dado', person: true },
        { label: 'Processor Number' },
        { label: '3rd Party Ordering' },
      ],
      right: [
        { label: 'Appraisal Coordinator' },
        { label: 'Lock Desk', value: 'Dana Reeves', person: true },
        { label: 'Vendor Coordinator' },
        { label: 'Processing Manager' },
        { label: 'Closing Coordinator' },
        { label: 'Funding Coordinator' },
        { label: 'Post-Closer' },
      ],
    },
  },
  'ln-1176': {
    information: {
      left: [
        { label: 'Last Contact Attempt', value: 'Aug 25, 2026 · 1:00 PM' },
        { label: 'Loan Purpose', value: 'Purchase' },
        { label: 'Loan Type (1st TD)', value: 'Conventional' },
        { label: 'Loan Term', value: '30' },
        { label: 'Rate (1st TD)', value: '6.500%' },
        { label: 'Monthly Payment (1st TD)', value: '$4,356.00' },
        { label: 'LTV', value: '75.000%' },
        { label: 'CLTV', value: '75.000%' },
        { label: 'MERS MIN Number', value: '100392610009463117' },
        { label: 'Status Indicator', indicator: 'red' },
      ],
      right: [
        { label: 'Lender (1st)', value: 'Rocket Pro TPO' },
        { label: 'Lender Loan #', value: '3319440287' },
        { label: 'FHA/VA Case #' },
        { label: 'Property Type', value: 'Single Family Residence' },
        { label: 'Loan Program (1st TD)', value: 'Conventional 30 Year Fixed' },
        { label: 'Purchase Price', value: '$918,700.00' },
        { label: 'Loan Amount (1st TD)', value: '$689,000.00' },
        { label: 'DTI Front End', value: '26.402%' },
        { label: 'DTI Back End', value: '38.910%' },
        { label: 'Indicator Reason', value: '9 Days in Submitted to UW' },
      ],
    },
    team: {
      left: [
        { label: 'Loan Officer', value: 'Milan Rakic', person: true },
        { label: 'Loan Officer Assistant', value: 'Renee Perez', person: true },
        { label: 'Loan Officer Number', value: '(305) 514-5242' },
        { label: 'Loan Processor #1', value: 'Renee Perez', person: true },
        { label: 'Manager', value: 'Calvin Dado', person: true },
        { label: 'Processor Number', value: '(305) 514-5290' },
        { label: '3rd Party Ordering', value: 'Ordered Aug 22' },
      ],
      right: [
        { label: 'Appraisal Coordinator', value: 'Nate Zook', person: true },
        { label: 'Lock Desk', value: 'Dana Reeves', person: true },
        { label: 'Vendor Coordinator', value: 'Nate Zook', person: true },
        { label: 'Processing Manager', value: 'Erin Vaughn', person: true },
        { label: 'Closing Coordinator' },
        { label: 'Funding Coordinator' },
        { label: 'Post-Closer' },
      ],
    },
  },
  'ln-1168': {
    information: {
      left: [
        { label: 'Last Contact Attempt', value: 'Aug 27, 2026 · 9:30 AM' },
        { label: 'Loan Purpose', value: 'Purchase' },
        { label: 'Loan Type (1st TD)', value: 'VA' },
        { label: 'Loan Term', value: '30' },
        { label: 'Rate (1st TD)', value: '5.990%' },
        { label: 'Monthly Payment (1st TD)', value: '$2,486.00' },
        { label: 'LTV', value: '100.000%' },
        { label: 'CLTV', value: '100.000%' },
        { label: 'MERS MIN Number', value: '100392610009455902' },
        { label: 'Status Indicator', indicator: 'green' },
      ],
      right: [
        { label: 'Lender (1st)', value: 'UWM' },
        { label: 'Lender Loan #', value: '1526379014' },
        { label: 'FHA/VA Case #', value: '10-10-6-0492871' },
        { label: 'Property Type', value: 'Single Family Residence' },
        { label: 'Loan Program (1st TD)', value: 'VA Elite 30 Year Fixed' },
        { label: 'Purchase Price', value: '$415,000.00' },
        { label: 'Loan Amount (1st TD)', value: '$415,000.00' },
        { label: 'DTI Front End', value: '22.870%' },
        { label: 'DTI Back End', value: '31.045%' },
        { label: 'Indicator Reason', value: '1 Day in Docs Out' },
      ],
    },
    team: {
      left: [
        { label: 'Loan Officer', value: 'Milan Rakic', person: true },
        { label: 'Loan Officer Assistant', value: 'Handy Nunez', person: true },
        { label: 'Loan Officer Number', value: '(305) 514-5242' },
        { label: 'Loan Processor #1', value: 'Renee Perez', person: true },
        { label: 'Manager', value: 'Calvin Dado', person: true },
        { label: 'Processor Number', value: '(305) 514-5290' },
        { label: '3rd Party Ordering', value: 'Complete' },
      ],
      right: [
        { label: 'Appraisal Coordinator', value: 'Nate Zook', person: true },
        { label: 'Lock Desk', value: 'Dana Reeves', person: true },
        { label: 'Vendor Coordinator', value: 'Nate Zook', person: true },
        { label: 'Processing Manager', value: 'Erin Vaughn', person: true },
        { label: 'Closing Coordinator', value: 'Sasha Bell', person: true },
        { label: 'Funding Coordinator', value: 'Omar Diaz', person: true },
        { label: 'Post-Closer' },
      ],
    },
  },
  'ln-1155': {
    information: {
      left: [
        { label: 'Last Contact Attempt', value: 'Aug 27, 2026 · 5:22 PM' },
        { label: 'Loan Purpose', value: 'Refinance' },
        { label: 'Loan Type (1st TD)', value: 'Conventional' },
        { label: 'Loan Term', value: '30' },
        { label: 'Rate (1st TD)', value: '6.624%' },
        { label: 'Monthly Payment (1st TD)', value: '$2,110.00' },
        { label: 'LTV', value: '79.721%' },
        { label: 'CLTV', value: '79.721%' },
        { label: 'MERS MIN Number' },
        { label: 'Status Indicator', indicator: 'amber' },
      ],
      right: [
        { label: 'Lender (1st)', value: 'UWM' },
        { label: 'Lender Loan #', value: '1526366401' },
        { label: 'FHA/VA Case #' },
        { label: 'Property Type', value: 'Single Family Residence' },
        { label: 'Loan Program (1st TD)', value: 'Conventional 30 Year Fixed' },
        { label: 'Purchase Price' },
        { label: 'Loan Amount (1st TD)', value: '$329,500.00' },
        { label: 'DTI Front End', value: '21.616%' },
        { label: 'DTI Back End', value: '29.461%' },
        { label: 'Indicator Reason', value: '5 Days in Conditions' },
      ],
    },
    team: {
      left: [
        { label: 'Loan Officer', value: 'Milan Rakic', person: true },
        { label: 'Loan Officer Assistant', value: 'Renee Perez', person: true },
        { label: 'Loan Officer Number', value: '(305) 514-5242' },
        { label: 'Loan Processor #1', value: 'Renee Perez', person: true },
        { label: 'Manager', value: 'Calvin Dado', person: true },
        { label: 'Processor Number', value: '(305) 514-5290' },
        { label: '3rd Party Ordering' },
      ],
      right: [
        { label: 'Appraisal Coordinator', value: 'Nate Zook', person: true },
        { label: 'Lock Desk' },
        { label: 'Vendor Coordinator', value: 'Nate Zook', person: true },
        { label: 'Processing Manager' },
        { label: 'Closing Coordinator' },
        { label: 'Funding Coordinator' },
        { label: 'Post-Closer' },
      ],
    },
  },
}
